import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
} from "@angular/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  //withHashLocation,
  provideRouter,
  withInMemoryScrolling,
} from "@angular/router";
import { TranslocoService, provideTransloco } from "@jsverse/transloco";
import player from "lottie-web";
import { provideLottieOptions } from "ngx-lottie";
import { firstValueFrom } from "rxjs";
// Fuse & custom providers
import { provideFuse } from "@fuse";
import { provideAuth } from "app/core/auth/auth.provider";
import { provideIcons } from "app/core/icons/icons.provider";
import { mockApiServices } from "app/mock-api";
// Project imports

import { appRoutes } from "app/app.routes";
import { TranslocoHttpLoader } from "./core/transloco/transloco.http-loader";
// MSAL imports
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { AuthInterceptor } from "./core/auth/auth.interceptor";
import { CacheBusterService } from "./core/services/cache-buster.service";
import { CookieService } from "./core/services/cookie.service";
import { GlobalErrorHandler } from "./core/services/global-error.handler";
import { LoggerService } from "./core/services/logger.service";
import { UserService } from "./core/user/user.service";
import { environment } from "./environments/environment";
import { registerLocaleData } from "@angular/common";
import localeDe from "@angular/common/locales/de";
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
} from "@angular/material/dialog";
import { AgGridModule } from "ag-grid-angular";
import { DialogConstant } from "./modules/installer/layout/common/constants/dialog-constant";
registerLocaleData(localeDe); // ✅ Register 'de' locale

/* -------------------------------------------------------------------
 * MSAL Factory & Config
 * ------------------------------------------------------------------- */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureB2C.clientId,
      authority: environment.azureB2C.authority,
      redirectUri: environment.azureB2C.redirectUri,
      knownAuthorities: environment.azureB2C.knownAuthorities,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback: (logLevel, message) => {
          //console.log(`MSAL Log (${logLevel}):`, message);
        },
        logLevel: 2,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  const clonedAuthRequest = {
    ...environment.azureB2C.authRequest,
    extraQueryParameters: {
      ...environment.azureB2C.authRequest?.extraQueryParameters,
      // Generate a fresh nonce on each creation
      //nonce: generateNonce(16)
    },
  };

  return {
    interactionType: InteractionType.Redirect,
    authRequest: clonedAuthRequest,
    loginFailedRoute: "/sign-in",
  };
}
export function msalAppInitializer(
  msalInstance: IPublicClientApplication,
  logger: LoggerService,
  userService: UserService,
  translocoService: TranslocoService,
  cookieService: CookieService,
): () => Promise<void> {
  return async () => {
    logger.debug("[MSAL] Bootstrapping MSAL...", "");
    await msalInstance.initialize();

    try {
      const response = await msalInstance.handleRedirectPromise();
      console.error('[PM-DEEPLINK] app.config: handleRedirectPromise done.',
        '| response?.account=', response?.account?.username ?? null,
        '| localStorage key=', localStorage.getItem('pending_pm_deeplink_after_login'),
        '| sessionStorage key=', sessionStorage.getItem('pending_pm_deeplink_after_login'));
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
        logger.debug(
          "[MSAL] Redirect response received for account:",
          response.account.username,
        );

        // Restore PM deep-link immediately — before Angular's router starts.
        const pendingPmUrl =
          localStorage.getItem('pending_pm_deeplink_after_login') ||
          sessionStorage.getItem('pending_pm_deeplink_after_login');
        console.error('[PM-DEEPLINK] app.config: fresh login, pendingPmUrl=', pendingPmUrl);
        if (pendingPmUrl) {
          const targetUrl = window.location.origin + pendingPmUrl;
          console.error('[PM-DEEPLINK] app.config: calling window.location.replace ->', targetUrl);
          window.location.replace(targetUrl);
          await new Promise<never>(() => {});
          return;
        }
      } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          logger.debug(
            "[MSAL] Set active account from cache:",
            accounts[0].username,
          );
        } else {
          logger.debug("[MSAL] No accounts available", "");
        }
      }

      // Pre-fetch user so portalRole is in localStorage before routing starts.
      // This prevents the AuthGuard from making an async API call on first load,
      // which caused a loading flicker on first login.
      if (msalInstance.getActiveAccount()) {
        const userLanguageCookieKey = "user_selected_language_code";
        const languageFromCookie = cookieService.getCookie(userLanguageCookieKey);
        const hasValidCookieLanguage =
          languageFromCookie === "de" || languageFromCookie === "en-GB";

        if (hasValidCookieLanguage) {
          translocoService.setActiveLang(languageFromCookie);
        }

        const applyUserLanguage = (languageName: string | undefined | null): void => {
          const normalizedLanguage = String(languageName ?? "").trim();
          const activeLang = normalizedLanguage === "Deutsch" ? "de" : "en-GB";
          translocoService.setActiveLang(activeLang);
          cookieService.setCookie(userLanguageCookieKey, activeLang);
        };

        const activeUsername = String(msalInstance.getActiveAccount()?.username ?? "")
          .trim()
          .toLowerCase();
        const cachedUserRaw = localStorage.getItem("loggedInUser");
        let cachedPortalRole = "";

        if (cachedUserRaw) {
          try {
            const cachedUser = JSON.parse(cachedUserRaw);
            const cachedEmail = String(cachedUser?.email ?? "")
              .trim()
              .toLowerCase();
            if (!activeUsername || !cachedEmail || cachedEmail === activeUsername) {
              cachedPortalRole = String(cachedUser?.portalRole ?? "").trim();
              if (!hasValidCookieLanguage) {
                applyUserLanguage(cachedUser?.languageName);
              }
            }
          } catch {
            cachedPortalRole = "";
          }
        }

        if (!cachedPortalRole) {
          try {
            const user = await firstValueFrom(userService.get());
            if (!hasValidCookieLanguage) {
              applyUserLanguage(user?.languageName);
            }
            logger.debug("[MSAL] User pre-fetched and cached", "");
          } catch (e) {
            logger.warn("[MSAL] User pre-fetch failed, guard will handle:", "");
          }
        }
      }
    } catch (error: any) {
      const errorCode = error?.errorCode ?? "unknown";
      // Recoverable in local/dev flows when no matching redirect state is cached.
      logger.warn(
        "[MSAL] handleRedirectPromise failed, continuing bootstrap:",
        errorCode,
      );

      // Stale redirect hash (for example old #state links) can trap users on loader.
      // Clear hash and continue with normal app routing.
      if (window.location.hash?.includes("state=")) {
        history.replaceState(
          null,
          document.title,
          `${window.location.pathname}${window.location.search}`,
        );
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
    logger.debug("[MSAL] Initialization complete", "");
  };
}

export function MSALInterceptorConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map<string, Array<string>>([
      [
        environment.azureB2C.protectedResources.api.endpoint,
        environment.azureB2C.protectedResources.api.scopes,
      ],
    ]),
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Cache-busting: runs first; reloads once on version change after a deployment.
    {
      provide: APP_INITIALIZER,
      useFactory: (cacheBuster: CacheBusterService) =>
        () => cacheBuster.checkAndClearIfVersionChanged(),
      deps: [CacheBusterService],
      multi: true,
    },

    importProvidersFrom(AgGridModule),
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(
      appRoutes,
      // withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" }),
    ),

    // Error handling
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: LOCALE_ID, useValue: "de" }, // ✅ Set the locale
    // Material date adapters
    { provide: DateAdapter, useClass: LuxonDateAdapter },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: { dateInput: "D" },
        display: {
          dateInput: "DDD",
          monthYearLabel: "LLL yyyy",
          dateA11yLabel: "DD",
          monthYearA11yLabel: "LLLL yyyy",
        },
      },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { panelClass: DialogConstant.PANEL_CLASS } as MatDialogConfig,
    },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntl },

    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // MSAL Initializer
    {
      provide: APP_INITIALIZER,
      useFactory: msalAppInitializer,
      deps: [MSAL_INSTANCE, LoggerService, UserService, TranslocoService, CookieService],
      multi: true,
    },

    // Transloco configuration
    provideTransloco({
      config: {
        availableLangs: [
          { id: "en-GB", label: "EN – English" },
          { id: "de", label: "DE – Deutsch" },
        ],
        defaultLang: "de",
        fallbackLang: "en",
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),

    // Transloco Initializer
    {
      provide: APP_INITIALIZER,
      useFactory: (translocoService: TranslocoService) => {
        return async () => {
          const activeLang =
            translocoService.getActiveLang() || translocoService.getDefaultLang();
          translocoService.setActiveLang(activeLang);
          await firstValueFrom(translocoService.load(activeLang));
        };
      },
      deps: [TranslocoService],
      multi: true,
    },

    // Fuse & UI Providers
    provideAuth(),
    provideIcons(),
    provideFuse({
      mockApi: { delay: 0, services: mockApiServices },
      fuse: {
        layout: "dense",
        scheme: "light",
        screens: { sm: "600px", md: "960px", lg: "1280px", xl: "1440px" },
        theme: "theme-default",
        themes: [{ id: "theme-default", name: "Default" }],
      },
    }),
    provideLottieOptions({ player: () => player }),

    // Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
};
